"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { useParams, useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { ApiError, api, hasSession } from "../../../lib/api";
import Avatar from "../../../components/Avatar";
import Icon, { Stars } from "../../../components/Icon";

// Leaflet toca window al importarse: solo puede cargar en el navegador
const MapaMini = dynamic(() => import("../../../components/MapaMini"), {
  ssr: false,
  loading: () => <div className="absolute inset-0 bg-[#101516] animate-pulse" />,
});

interface Service { id: string; name: string; shopPriceGs?: number | null; homePriceGs?: number | null }
interface Review { rating: number; comment?: string | null; createdAt: string; author: { fullName: string } }
interface Profile {
  id: string; displayName: string; bio?: string | null; city?: string | null;
  lat?: number | null; lng?: number | null;
  coverageKm: number; homeSurchargePct: number; isAvailable: boolean;
  ratingAvg: number; ratingCount: number; jobsDone: number; distanceKm: number | null;
  user: { id: string; kycLevel: number };
  especialidades?: { id: string; name: string }[];
  services: Service[]; reviews: Review[];
}
interface CreatedRequest { id: string }

const gs = (n?: number | null) => (n == null ? "—" : `₲ ${n.toLocaleString("es-PY")}`);

/** El ?next= vuelve al usuario a donde estaba después de ingresar. Se valida que
 *  sea una ruta de la app: un "//otro-sitio" o un "https://…" convertiría
 *  nuestro login en un trampolín para llevarse gente a otro lado. */
function volverA(ruta: string) {
  const segura = ruta.startsWith("/") && !ruta.startsWith("//") ? ruta : "/";
  return `/ingresar?next=${encodeURIComponent(segura)}`;
}

const MAX_DIRECCION = 300;

export default function PerfilTecnico() {
  const { id } = useParams<{ id: string }>();
  const router = useRouter();
  const [p, setP] = useState<Profile | null>(null);
  const [error, setError] = useState("");
  const [errorCarga, setErrorCarga] = useState("");
  const [solicitar, setSolicitar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ deviceBrand: "", deviceModel: "", faultDescription: "", mode: "SHOP", customerAddress: "" });

  const cargar = useCallback(() => {
    setErrorCarga("");
    setP(null);
    api<Profile>(`/directory/tech/${id}`)
      .then(setP)
      .catch((e) =>
        setErrorCarga(
          e instanceof ApiError
            ? e.message
            : "No pudimos conectarnos. Revisá tu conexión y probá de nuevo.",
        ),
      );
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const aDomicilio = f.mode === "HOME";
  const faltaDireccion = aDomicilio && !f.customerAddress.trim();

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!hasSession()) { router.push(volverA(`/tec/${id}`)); return; }
    if (faltaDireccion) { setError("Escribí la dirección donde el técnico tiene que ir"); return; }
    setError(""); setBusy(true);
    try {
      const pos = await new Promise<GeolocationPosition | null>((res) => {
        if (!navigator.geolocation) return res(null);
        navigator.geolocation.getCurrentPosition((x) => res(x), () => res(null), { timeout: 4000 });
      });
      const direccion = aDomicilio ? f.customerAddress.trim().slice(0, MAX_DIRECCION) : "";
      const r = await api<CreatedRequest>("/requests", {
        method: "POST",
        body: JSON.stringify({
          techId: id,
          deviceBrand: f.deviceBrand,
          deviceModel: f.deviceModel,
          // La dirección va también acá adentro a propósito: hoy el API no
          // guarda customerAddress en la solicitud (el campo existe en Ticket,
          // no en ServiceRequest) y sin esto el técnico acepta sin saber adónde
          // ir y termina pidiéndola por chat. Sacar este agregado el día que
          // /requests persista customerAddress.
          faultDescription: direccion
            ? `${f.faultDescription}\n\nDirección: ${direccion}`
            : f.faultDescription,
          mode: f.mode,
          customerAddress: direccion || undefined,
          lat: pos?.coords.latitude, lng: pos?.coords.longitude,
        }),
      });
      router.push(`/r/${r.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No pudimos enviar la solicitud. Probá de nuevo.");
      setBusy(false);
    }
  }

  const volver = (
    <Link href="/buscar" aria-label="Volver a la búsqueda"
      className="w-11 h-11 flex-none rounded-full border border-white/15 flex items-center justify-center text-[#B5C0BB] hover:bg-white/5">
      <Icon name="chevron-left" size={20} />
    </Link>
  );

  // Estado de error con salida: antes cualquier fallo dejaba un "Cargando…" gris
  // sin manera de reintentar ni de volver al listado.
  if (errorCarga) {
    return (
      <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] px-4 py-6">
        <div className="max-w-md mx-auto flex flex-col gap-5">
          <header className="flex items-center gap-3">{volver}<h1 className="text-xl font-bold tracking-tight">Perfil del técnico</h1></header>
          <div className="rounded-xl bg-[#1A2123] border border-white/10 p-8 text-center">
            <Icon name="x-circle" size={36} className="text-[#DE705C] mx-auto" strokeWidth={1.25} />
            <p className="mt-3 text-[14px] text-[#B5C0BB]">{errorCarga}</p>
            <div className="mt-5 flex flex-col gap-2">
              <button onClick={cargar}
                className="min-h-[44px] rounded-xl bg-[#E8A23C] text-[#201404] font-bold hover:brightness-105">
                Reintentar
              </button>
              <Link href="/buscar"
                className="min-h-[44px] rounded-xl border border-white/15 font-semibold flex items-center justify-center hover:bg-white/5">
                Buscar otro técnico
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!p) {
    return (
      <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] px-4 py-6" aria-busy="true">
        <div className="max-w-md mx-auto flex flex-col gap-5">
          <header className="flex items-center gap-3">
            {volver}
            <div className="w-14 h-14 rounded-full bg-white/6 animate-pulse flex-none" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-2/3 bg-white/6 rounded animate-pulse" />
              <div className="h-3 w-1/2 bg-white/6 rounded animate-pulse" />
            </div>
          </header>
          <div className="h-7 w-3/4 bg-white/6 rounded-full animate-pulse" />
          <div className="rounded-xl bg-[#1A2123] border border-white/10 p-4 space-y-3">
            {[...Array(3)].map((_, i) => <div key={i} className="h-4 bg-white/6 rounded animate-pulse" />)}
          </div>
          <div className="h-12 bg-white/6 rounded-xl animate-pulse" />
        </div>
      </main>
    );
  }

  const input = "w-full rounded-xl bg-[#101516] border border-white/15 px-4 py-3 text-[15px] placeholder-[#808D88] focus:outline-none focus:border-[#E8A23C]";

  return (
    <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] px-4 py-6 sm:p-6 pb-24">
      <div className="max-w-md mx-auto flex flex-col gap-5">
        <header className="flex items-center gap-3">
          {volver}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Avatar userId={p.user.id} nombre={p.displayName} size={56} className="!rounded-full" />
            <div className="min-w-0">
              <h1 className="text-xl font-bold tracking-tight leading-tight break-words">{p.displayName}</h1>
              <p className="text-[13px] text-[#808D88]">
                {p.ratingCount > 0 ? <><span className="inline-flex items-center gap-1 align-middle"><Icon name="star" size={13} /> {p.ratingAvg}</span> ({p.ratingCount} reseñas) · </> : ""}
                {p.jobsDone} trabajos{p.distanceKm != null && <> · a {p.distanceKm} km</>}
              </p>
            </div>
          </div>
          <span className={`w-3 h-3 rounded-full flex-none ${p.isAvailable ? "bg-[#5CBE8F]" : "bg-[#808D88]"}`}
            title={p.isAvailable ? "Recibiendo equipos ahora" : "No disponible"} />
        </header>

        <div className="flex gap-1.5 flex-wrap">
          {p.user.kycLevel >= 1 && <span className="text-2xs font-semibold bg-[#5CBE8F]/15 text-[#5CBE8F] rounded-full px-2.5 py-1 inline-flex items-center gap-1.5"><Icon name="check" size={14} /> Identidad y antecedentes verificados</span>}
          {p.user.kycLevel >= 3 && <span className="text-2xs font-semibold bg-[#E8A23C]/15 text-[#E8A23C] rounded-full px-2.5 py-1 inline-flex items-center gap-1.5"><Icon name="badge-check" size={14} /> Oficio validado</span>}
        </div>

        {(p.especialidades?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-1.5 -mt-1">
            {p.especialidades!.map((e) => (
              <span key={e.id} className="text-[12px] font-semibold bg-white/6 border border-white/10 rounded-full px-2.5 py-1 text-[#B5C0BB]">
                {e.name}
              </span>
            ))}
          </div>
        )}
        {p.bio && <p className="text-[14px] leading-relaxed text-[#B5C0BB]">{p.bio}</p>}

        {p.services.length > 0 && (
          <section className="rounded-xl bg-[#1A2123] border border-white/10 px-4 py-1">
            {p.services.map((s) => (
              <div key={s.id} className="flex justify-between items-baseline gap-3 py-3 border-b border-white/5 last:border-0">
                <span className="text-[14px] min-w-0">{s.name}</span>
                <span className="font-mono text-[13px] text-right flex-none">
                  {gs(s.shopPriceGs)} <span className="text-[#808D88]">local</span>
                  {s.homePriceGs != null && <> · {gs(s.homePriceGs)} <span className="text-[#808D88]">dom.</span></>}
                </span>
              </div>
            ))}
          </section>
        )}

        <div className="rounded-xl bg-[#5CBE8F]/10 px-4 py-3 text-[13.5px] flex items-start gap-2">
          <Icon name="shield-check" size={18} className="flex-none mt-px" />
          <span><strong>Garantía de 30 días</strong> en los trabajos presupuestados por la app.</span>
        </div>

        {!solicitar ? (
          <button
            onClick={() => (hasSession() ? setSolicitar(true) : router.push(volverA(`/tec/${id}`)))}
            className="rounded-xl bg-[#E8A23C] text-[#201404] font-bold py-3.5 min-h-[48px] hover:brightness-105">
            Solicitar reparación
          </button>
        ) : (
          <form onSubmit={enviar} className="rounded-xl bg-[#1A2123] border border-[#E8A23C]/40 p-4 flex flex-col gap-3">
            <strong className="text-[15px]">Tu solicitud a {p.displayName}</strong>
            <div className="grid grid-cols-2 gap-2">
              <input className={input} placeholder="Marca" aria-label="Marca del equipo" value={f.deviceBrand} onChange={(e) => setF({ ...f, deviceBrand: e.target.value })} required />
              <input className={input} placeholder="Modelo" aria-label="Modelo del equipo" value={f.deviceModel} onChange={(e) => setF({ ...f, deviceModel: e.target.value })} required />
            </div>
            <textarea className={input} rows={3} placeholder="¿Qué le pasa al equipo?" aria-label="Qué le pasa al equipo" value={f.faultDescription}
              onChange={(e) => setF({ ...f, faultDescription: e.target.value })} required />
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setF({ ...f, mode: "SHOP" })} aria-pressed={f.mode === "SHOP"}
                className={`rounded-xl border p-3 text-left ${f.mode === "SHOP" ? "border-[#E8A23C] bg-[#E8A23C]/10" : "border-white/15"}`}>
                <div className="text-sm font-bold inline-flex items-center gap-1.5"><Icon name="store" size={16} /> En su local</div>
                <div className="text-[13px] text-[#808D88]">llevás el equipo</div>
              </button>
              <button type="button" onClick={() => setF({ ...f, mode: "HOME" })} aria-pressed={aDomicilio}
                className={`rounded-xl border p-3 text-left ${aDomicilio ? "border-[#E8A23C] bg-[#E8A23C]/10" : "border-white/15"}`}>
                <div className="text-sm font-bold inline-flex items-center gap-1.5"><Icon name="home" size={16} /> A domicilio</div>
                <div className="text-[13px] text-[#808D88]">+{p.homeSurchargePct}% aprox.</div>
              </button>
            </div>
            {/* A domicilio sin dirección el técnico acepta el trabajo sin saber
                adónde ir: se pide acá y no por chat media hora después. */}
            {aDomicilio && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="direccion" className="text-[13px] font-semibold text-[#B5C0BB]">
                  ¿Adónde tiene que ir?
                </label>
                <input id="direccion" className={input} maxLength={MAX_DIRECCION} required
                  autoComplete="street-address"
                  placeholder="Calle, número, barrio y una referencia"
                  value={f.customerAddress}
                  onChange={(e) => setF({ ...f, customerAddress: e.target.value })} />
                <span className="text-[13px] text-[#808D88]">
                  Una referencia (“casa de portón verde, frente a la despensa”) le ahorra vueltas.
                </span>
              </div>
            )}
            {error && <p className="text-[13.5px] text-[#DE705C] bg-[#DE705C]/10 rounded-lg px-4 py-2.5">{error}</p>}
            <button disabled={busy || faltaDireccion}
              className="rounded-xl bg-[#E8A23C] text-[#201404] font-bold py-3 min-h-[48px] hover:brightness-105 disabled:opacity-50">
              {busy ? "Enviando…" : "Enviar solicitud"}
            </button>
            <button type="button" onClick={() => { setSolicitar(false); setError(""); }} disabled={busy}
              className="text-[13.5px] font-semibold text-[#B5C0BB] min-h-[44px] hover:text-[#EDF1EF] disabled:opacity-50">
              Cancelar
            </button>
          </form>
        )}

        {/* Dónde queda: el pin del taller y hasta dónde llega. "Atiende hasta
            8 km" escrito no dice nada; el círculo sobre TU barrio sí. El botón
            de "cómo llegar" abre la app de mapas del teléfono: gratis, sin
            claves, y es lo que el cliente usa el día que retira su equipo. */}
        {p.lat != null && p.lng != null && (
          <section>
            <h2 className="font-mono text-[13px] tracking-[0.15em] uppercase text-[#808D88] mb-2">
              Dónde queda
            </h2>
            <div className="rounded-2xl border border-white/10 overflow-hidden">
              <div className="relative h-52">
                <MapaMini lat={p.lat} lng={p.lng} coverageKm={p.coverageKm} />
              </div>
              <div className="flex items-center gap-3 bg-[#1A2123] px-4 py-3 flex-wrap">
                <div className="min-w-0 flex-1 text-[13px] text-[#B5C0BB]">
                  {p.city && <span className="font-semibold text-[#EDF1EF]">{p.city}</span>}
                  {p.coverageKm > 0 && <> · atiende hasta {p.coverageKm} km</>}
                  {p.distanceKm != null && <> · a {p.distanceKm} km tuyo</>}
                </div>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-4 text-[13px] font-bold hover:bg-white/10 flex-none"
                >
                  <Icon name="map-pin" size={15} className="text-[#E8A23C]" /> Cómo llegar
                </a>
              </div>
            </div>
          </section>
        )}

        {p.reviews.length > 0 && (
          <section>
            <h2 className="font-mono text-[13px] tracking-[0.15em] uppercase text-[#808D88] mb-2">Reseñas</h2>
            <div className="flex flex-col gap-2">
              {p.reviews.map((r, i) => (
                <div key={i} className="rounded-xl bg-[#1A2123] border border-white/10 p-3.5">
                  <div className="text-[13.5px]"><Stars value={r.rating} size={13} /> {r.comment && `"${r.comment}"`}</div>
                  <div className="text-[13px] text-[#808D88] mt-1">
                    {r.author.fullName} · {new Date(r.createdAt).toLocaleDateString("es-PY")} · trabajo verificado
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
